import { createAccessGroup, readAccessGroups, type AccessGroup } from '@/api/core/access-groups';
import { syncAccess, type AccessSyncResult } from '@/api/core/access-sync';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { isUuid } from '@/lib/validation';
import {
  Button,
  Input,
  ListPage,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { propertyRuntime, type PropertyRuntime } from '../property/property-queries';

/** 권한 그룹과 명시적으로 확인한 동기화 계획을 표시한다. */
export function AccessGroupsPage({ runtime = propertyRuntime }: { runtime?: PropertyRuntime }) {
  const session = useOperatorSession(runtime.session);
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<{ generation: number; result: AccessSyncResult }>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [cursor, setCursor] = useState<string>();
  const [name, setName] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [floor, setFloor] = useState('');
  const [kind, setKind] = useState<AccessGroup['kind']>('RESIDENT');
  const [staffRole, setStaffRole] = useState<NonNullable<AccessGroup['staff_role']>>('ADMIN');
  const allowed =
    session.status === 'authorized' &&
    session.authorization !== null &&
    !session.deniedDomains.includes('entitlement') &&
    canReadDomain(session.authorization, 'entitlement', 1);
  const writable =
    session.status === 'authorized' &&
    session.authorization !== null &&
    !session.deniedDomains.includes('entitlement') &&
    canReadDomain(session.authorization, 'entitlement', 4);
  const currentPermission = (bit: number) => {
    const current = runtime.session.getSnapshot();
    return (
      current.generation === session.generation &&
      current.status === 'authorized' &&
      !!current.authorization &&
      !current.deniedDomains.includes('entitlement') &&
      canReadDomain(current.authorization, 'entitlement', bit)
    );
  };
  const key = [
    ...coreQueryKey(runtime.apiNamespace, session.generation, 'access-groups'),
    cursor ?? '',
  ];
  const groups = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => {
      if (!currentPermission(1)) throw new Error('현재 권한을 확인할 수 없습니다.');
      try {
        const result = await readAccessGroups(runtime.client, cursor, signal);
        if (!currentPermission(1)) throw new Error('현재 권한을 확인할 수 없습니다.');
        return result;
      } catch (error) {
        if (isApiError(error) && error.status === 403 && !signal.aborted && currentPermission(1))
          runtime.session.reportDomainAccessDenied('entitlement', session.generation);
        throw error;
      }
    },
    enabled: allowed,
  });
  const runSync = async (dryRun: boolean) => {
    if (
      !writable ||
      !currentPermission(4) ||
      busy ||
      (!dryRun && preview?.generation !== session.generation)
    )
      return;
    const generation = session.generation;
    setBusy(true);
    setMessage('');
    try {
      const result = await syncAccess(runtime.client, dryRun);
      if (!currentPermission(4)) return;
      if (dryRun) setPreview({ generation, result });
      else {
        setPreview(undefined);
        setMessage('동기화를 완료했습니다.');
        await queryClient.invalidateQueries({ queryKey: key });
      }
    } catch (error) {
      if (runtime.session.getSnapshot().generation !== generation) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('entitlement', generation);
      setPreview(undefined);
      setMessage(
        isApiError(error) && error.status === 409
          ? '동기화 상태가 변경되었습니다. 다시 미리보세요.'
          : '동기화를 수행하지 못했습니다.',
      );
    } finally {
      setBusy(false);
    }
  };
  const create = async () => {
    if (!writable || !currentPermission(4) || busy) return;
    const floorValue = Number(floor);
    if (
      !name.trim() ||
      (propertyId !== '' && !isUuid(propertyId)) ||
      (kind !== 'STAFF' &&
        (!isUuid(propertyId) || floor.trim() === '' || !Number.isSafeInteger(floorValue)))
    ) {
      setMessage('그룹 이름, 지점과 층을 확인해 주세요.');
      return;
    }
    const generation = session.generation;
    setBusy(true);
    setMessage('');
    try {
      await createAccessGroup(runtime.client, {
        command_id: crypto.randomUUID(),
        id: crypto.randomUUID(),
        name: name.trim(),
        property_id: propertyId || null,
        kind,
        staff_role: kind === 'STAFF' ? staffRole : null,
        floor: kind === 'STAFF' ? null : floorValue,
      });
      if (!currentPermission(4)) return;
      setName('');
      setMessage('출입 그룹을 생성했습니다.');
      await queryClient.invalidateQueries({ queryKey: key });
    } catch (error) {
      if (runtime.session.getSnapshot().generation !== generation) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('entitlement', generation);
      setMessage(
        isApiError(error) && error.status === 409
          ? '이미 존재하거나 변경된 그룹입니다. 다시 조회해 주세요.'
          : '그룹을 생성하지 못했습니다.',
      );
    } finally {
      setBusy(false);
    }
  };
  if (session.status !== 'authorized') return null;
  return (
    <ListPage className="min-h-0 flex-1 overflow-y-auto">
      <ShellHeader>
        <PageHeader title="출입 그룹" description="그룹과 기기별 출입 규칙을 관리합니다." />
      </ShellHeader>
      <div className="space-y-4 p-4">
        {allowed ? null : <p role="alert">출입 그룹 조회 권한이 없습니다.</p>}
        {writable ? (
          <section className="space-y-2" aria-label="그룹 생성">
            <label>
              그룹 이름
              <Input
                value={name}
                maxLength={100}
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            </label>
            <label>
              지점 UUID
              <Input
                value={propertyId}
                onChange={(event) => {
                  setPropertyId(event.target.value);
                }}
              />
            </label>
            <Select
              value={kind}
              onValueChange={(value) => {
                if (value === 'RESIDENT' || value === 'GUEST' || value === 'STAFF') setKind(value);
              }}
            >
              <SelectTrigger aria-label="그룹 종류">
                <SelectValue>{kind}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="RESIDENT">입주자</SelectItem>
                <SelectItem value="GUEST">게스트</SelectItem>
                <SelectItem value="STAFF">직원</SelectItem>
              </SelectContent>
            </Select>
            {kind === 'STAFF' ? (
              <Select
                value={staffRole}
                onValueChange={(value) => {
                  if (
                    value === 'ADMIN' ||
                    value === 'MANAGER' ||
                    value === 'FM' ||
                    value === 'HK' ||
                    value === 'GUARD' ||
                    value === 'RETAIL'
                  )
                    setStaffRole(value);
                }}
              >
                <SelectTrigger aria-label="직원 역할">
                  <SelectValue>{staffRole}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {(['ADMIN', 'MANAGER', 'FM', 'HK', 'GUARD', 'RETAIL'] as const).map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <label>
                층
                <Input
                  type="number"
                  value={floor}
                  onChange={(event) => {
                    setFloor(event.target.value);
                  }}
                />
              </label>
            )}
            <Button disabled={busy} onClick={() => void create()}>
              그룹 생성
            </Button>
          </section>
        ) : null}
        {writable ? (
          <Button disabled={busy} onClick={() => void runSync(true)}>
            동기화 미리보기
          </Button>
        ) : null}
        {preview?.generation === session.generation ? (
          <section aria-label="동기화 미리보기">
            <p>
              생성 {preview.result.created} · 변경 {preview.result.updated} · 삭제{' '}
              {preview.result.removed}
            </p>
            <p>
              기기 누락 {preview.result.skipped_missing_device} · 지점 간 규칙{' '}
              {preview.result.cross_branch_rules}
            </p>
            <p>대응 지점 없음: {preview.result.skipped_missing_branches.join(', ') || '없음'}</p>
            <Button disabled={busy} onClick={() => void runSync(false)}>
              동기화 실행
            </Button>
          </section>
        ) : null}
        {message ? <p role="status">{message}</p> : null}
        {allowed && groups.isPending ? <p role="status">출입 그룹을 조회하고 있습니다.</p> : null}
        {groups.isError ? <p role="alert">출입 그룹을 불러오지 못했습니다.</p> : null}
        {allowed && groups.data ? (
          <>
            <ul>
              {groups.data.items.map((group) => (
                <li key={group.id}>
                  <Link to={`/access/groups/${group.id}`}>{group.name}</Link> · {group.kind}{' '}
                  {group.floor === null ? '' : `${String(group.floor)}층`}{' '}
                  {group.origin === 'LEGACY_SYNC' ? '· 동기화됨 (읽기 전용)' : ''}
                </li>
              ))}
            </ul>
            {groups.data.next_cursor ? (
              <Button
                onClick={() => {
                  setCursor(groups.data.next_cursor ?? undefined);
                }}
              >
                다음 그룹
              </Button>
            ) : null}
            {cursor ? (
              <Button
                variant="outline"
                onClick={() => {
                  setCursor(undefined);
                }}
              >
                처음 그룹
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
    </ListPage>
  );
}
